import React, { useCallback, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';

import {
  FileWithPreview,
  ImageDropzone,
} from '../../../components/design-system';
import {
  ADD_SITE_IMAGES_MUTATION,
  DELETE_SITE_IMAGE_MUTATION,
  GET_CLOUDFLARE_DIRECT_LINK_QUERY,
  SET_SITE_MAIN_IMAGE_MUTATION,
} from '../../../gql';
import { handleUploadImage } from '../../../io';
import { SiteImageGallery } from './SiteImageGallery';
import { FormattedMessage, useIntl } from 'react-intl';

interface SiteImagesProps {
  called: boolean;
  loading: boolean;
  siteId?: string;
  site?: {
    image?: string | null;
    images?: string[];
    shared?: boolean;
  };
}

const acceptFiles = () => undefined;

const SiteImages = React.memo(
  ({ called, loading, siteId, site }: SiteImagesProps) => {
    const intl = useIntl();
    const client = useApolloClient();
    const [files, setFiles] = useState<FileWithPreview[]>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string>();
    const [addSiteImages] = useMutation(ADD_SITE_IMAGES_MUTATION);
    const [deleteSiteImage, { loading: deleting }] = useMutation(
      DELETE_SITE_IMAGE_MUTATION
    );
    const [setSiteMainImage, { loading: settingMain }] = useMutation(
      SET_SITE_MAIN_IMAGE_MUTATION
    );
    const readOnly = Boolean(site?.shared);

    const handleUpload = useCallback(async () => {
      if (!siteId || files.length === 0) {
        return;
      }

      setError(undefined);
      setUploading(true);

      try {
        const uploadedImageIds = await Promise.all(
          files.map(async (file) => {
            try {
              const { data } = await client.query({
                query: GET_CLOUDFLARE_DIRECT_LINK_QUERY,
                fetchPolicy: 'no-cache',
                variables: { name: file.name },
              });
              const { uploadURL, id } = data.getCloudflareDirectLink;

              if (!uploadURL || !id) {
                throw new Error('Failed to retrieve a direct upload URL.');
              }

              return handleUploadImage(file, uploadURL, id);
            } catch (imageUploadError) {
              console.error(imageUploadError);

              return undefined;
            }
          })
        );
        const imageIds = uploadedImageIds.filter((imageId): imageId is string =>
          Boolean(imageId)
        );

        if (imageIds.length === 0) {
          throw new Error('No images were uploaded.');
        }

        const { data } = await addSiteImages({
          variables: { siteId, imageIds },
        });

        if (!data?.addSiteImages?.site) {
          throw new Error(
            'The uploaded images could not be added to the Site.'
          );
        }

        setFiles([]);
      } catch (uploadError) {
        console.error(uploadError);
        setError(
          intl.formatMessage({
            id: 'site.images.uploadError',
            defaultMessage: 'Uploading images failed. Please try again.',
          })
        );
      } finally {
        setUploading(false);
      }
    }, [addSiteImages, client, files, intl, siteId]);

    const handleDelete = useCallback(
      async (imageId: string) => {
        if (
          !siteId ||
          !window.confirm(
            intl.formatMessage(
              {
                id: 'site.images.confirmDelete',
                defaultMessage: 'Are you sure you want to delete this image?',
              },
              { imageId }
            )
          )
        ) {
          return;
        }

        setError(undefined);

        try {
          const { data } = await deleteSiteImage({
            variables: { siteId, imageId },
          });

          if (!data?.deleteSiteImage?.site) {
            throw new Error('The image could not be deleted.');
          }
        } catch (deleteError) {
          console.error(deleteError);
          setError(
            intl.formatMessage({
              id: 'image.delete.error',
              defaultMessage: 'Deleting the image failed. Please try again.',
            })
          );
        }
      },
      [deleteSiteImage, intl, siteId]
    );

    const handleSetMain = useCallback(
      async (imageId: string) => {
        if (!siteId) {
          return;
        }

        setError(undefined);

        try {
          const { data } = await setSiteMainImage({
            variables: { siteId, imageId },
          });

          if (!data?.setSiteMainImage?.site) {
            throw new Error('The main image could not be updated.');
          }
        } catch (mainImageError) {
          console.error(mainImageError);
          setError(
            intl.formatMessage({
              id: 'site.images.mainImageError',
              defaultMessage:
                'Updating the main image failed. Please try again.',
            })
          );
        }
      },
      [intl, setSiteMainImage, siteId]
    );

    if (loading) {
      return <CircularProgress size={24} />;
    }

    if (called && !site) {
      return (
        <Alert severity="warning">
          <FormattedMessage
            id="site.notFoundPunctuated"
            defaultMessage="Site not found."
          />
        </Alert>
      );
    }

    return (
      <Stack gap={2}>
        {!readOnly && (
          <Stack gap={1} alignItems="flex-start">
            <ImageDropzone
              files={files}
              setFiles={setFiles}
              onFilesAccept={acceptFiles}
            />
            <Button
              variant="contained"
              disabled={uploading || files.length === 0}
              onClick={handleUpload}
            >
              {uploading ? (
                <FormattedMessage
                  id="site.images.uploading"
                  defaultMessage="Uploading…"
                />
              ) : (
                <FormattedMessage
                  id="site.images.upload"
                  defaultMessage="Upload images"
                />
              )}
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <SiteImageGallery
          images={site?.images || []}
          mainImage={site?.image}
          readOnly={readOnly}
          actionsDisabled={deleting || settingMain}
          onDelete={handleDelete}
          onSetMain={handleSetMain}
        />
      </Stack>
    );
  }
);

export { SiteImages };
